import { useState } from 'react';
import { X, Banknote, Loader2 } from 'lucide-react';
import { colors } from '../constants/colors';
import { createPayout } from '../api/client';

interface RequestPayoutModalProps {
  availableBalance: number;
  bankName: string | null;
  bankLast4: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function RequestPayoutModal({
  availableBalance,
  bankName,
  bankLast4,
  onClose,
  onSuccess,
}: RequestPayoutModalProps) {
  const [amount, setAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const parsedAmount = parseFloat(amount);
  const isValidAmount = !isNaN(parsedAmount) && parsedAmount >= 1.0 && parsedAmount <= availableBalance;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidAmount) return;

    setIsLoading(true);
    setError(null);

    try {
      await createPayout(parsedAmount);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create payout');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.headerIcon}>
            <Banknote size={24} color={colors.brand.blue} />
          </div>
          <div>
            <h2 style={styles.title}>Request Payout</h2>
            <p style={styles.subtitle}>Transfer funds to your bank account</p>
          </div>
          <button style={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div style={styles.content}>
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.balanceRow}>
              <span style={styles.balanceLabel}>Available Balance</span>
              <span style={styles.balanceValue}>{formatCurrency(availableBalance)}</span>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Payout Amount</label>
              <div style={styles.inputWrapper}>
                <span style={styles.inputPrefix}>$</span>
                <input
                  type="number"
                  step="0.01"
                  min="1.00"
                  max={availableBalance}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  style={styles.input}
                  autoFocus
                />
              </div>
              <span style={styles.inputHint}>$1.00 minimum</span>
            </div>

            {bankName && bankLast4 && (
              <div style={styles.bankRow}>
                <span style={styles.bankLabel}>Destination</span>
                <span style={styles.bankValue}>{bankName} ****{bankLast4}</span>
              </div>
            )}

            <p style={styles.arrivalText}>
              Funds typically arrive in 1-3 business days.
            </p>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.actions}>
              <button
                type="button"
                onClick={onClose}
                style={styles.cancelButton}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  ...styles.submitButton,
                  opacity: !isValidAmount || isLoading ? 0.5 : 1,
                  cursor: !isValidAmount || isLoading ? 'not-allowed' : 'pointer',
                }}
                disabled={!isValidAmount || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                    Processing...
                  </>
                ) : (
                  'Request Payout'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '20px 20px 0 20px',
  },
  headerIcon: {
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.brand.blue10,
    borderRadius: '12px',
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    color: colors.gray[600],
  },
  closeButton: {
    marginLeft: 'auto',
    padding: '8px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: colors.gray[400],
  },
  content: {
    padding: '20px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  balanceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: colors.gray[100],
    borderRadius: '8px',
  },
  balanceLabel: {
    fontSize: '14px',
    color: colors.gray[600],
  },
  balanceValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  inputLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '10px',
    overflow: 'hidden',
  },
  inputPrefix: {
    padding: '14px 0 14px 14px',
    fontSize: '16px',
    color: colors.gray[600],
    backgroundColor: colors.white,
  },
  input: {
    flex: 1,
    padding: '14px 14px 14px 4px',
    fontSize: '16px',
    border: 'none',
    outline: 'none',
    color: colors.ink.black,
    backgroundColor: colors.white,
    textAlign: 'right',
  },
  inputHint: {
    fontSize: '12px',
    color: colors.gray[600],
    textAlign: 'right',
  },
  bankRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: colors.gray[100],
    borderRadius: '8px',
  },
  bankLabel: {
    fontSize: '14px',
    color: colors.gray[600],
  },
  bankValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  arrivalText: {
    margin: 0,
    fontSize: '13px',
    color: colors.gray[600],
    lineHeight: 1.4,
  },
  error: {
    padding: '12px',
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '8px',
    fontSize: '14px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '4px',
  },
  cancelButton: {
    flex: 1,
    padding: '14px',
    fontSize: '15px',
    fontWeight: 600,
    color: colors.gray[700],
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  submitButton: {
    flex: 1,
    padding: '14px',
    fontSize: '15px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
};
